"use client";

import { Button } from "@workspace/ui/components/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@workspace/ui/components/card";
import { cn } from "@workspace/ui/lib";
import { AnimatePresence, motion } from "framer-motion";
import { usePostHog } from "posthog-js/react";
import { useEffect } from "react";
import { useLocalStorage } from "usehooks-ts";
import { LOCAL_STORAGE_KEYS } from "~/utils/consts";
import trackEvent, { EventNames } from "~/utils/trackEvent";

export function cookieConsentGiven() {
  if (typeof window === "undefined") {
    return null;
  }

  if (
    window.localStorage &&
    !window.localStorage.getItem(LOCAL_STORAGE_KEYS.CookieConsent)
  ) {
    return null;
  }
  const cookieConsentValue = localStorage.getItem(
    LOCAL_STORAGE_KEYS.CookieConsent
  );
  if (cookieConsentValue === "true") {
    return true;
  } else if (cookieConsentValue === "false") {
    return false;
  }
  return null;
}

export function CookieConsentCard({ className }: { className?: string }) {
  const [isConsentGiven, setIsConsentGiven] = useLocalStorage<boolean | null>(
    LOCAL_STORAGE_KEYS.CookieConsent,
    null,
    { initializeWithValue: false }
  );
  const posthog = usePostHog();

  useEffect(() => {
    if (isConsentGiven !== null) {
      posthog.set_config({
        persistence: isConsentGiven ? "localStorage+cookie" : "memory",
      });
    }
  }, [isConsentGiven, posthog]);

  const handleAcceptCookies = () => {
    setIsConsentGiven(true);
  };

  const handleDeclineCookies = () => {
    setIsConsentGiven(false);
    trackEvent(EventNames.COOKIES_DECLINED);
  };

  return (
    <AnimatePresence>
      {isConsentGiven == null && (
        <motion.div
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ opacity: 0, y: 400 }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
          className="z-30"
        >
          <Card className={cn("w-96 bg-card", className)}>
            <CardHeader>
              <CardTitle className="flex gap-1">
                <span>🍪</span>
                <span>Cookies</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p>
                We use cookies to help us improve your experience on CodeWords.
                We do not track you or sell your data. Please see our{" "}
                <a
                  className="underline"
                  target="_blank"
                  href="https://agemo.ai/legal#cookie"
                >
                  Privacy Policy
                </a>{" "}
                for more information.
              </p>
            </CardContent>
            <CardFooter className="flex justify-between">
              <Button className="mr-2" onClick={handleAcceptCookies}>
                Accept cookies
              </Button>
              <Button variant="outline" onClick={handleDeclineCookies}>
                Decline cookies
              </Button>
            </CardFooter>
          </Card>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
